/**
 * provenance — the label-synthetic treatment for generated metrics
 * (UIR-27, Voyage #212/#188/#191/#200, ratified disposition 2026-07-25).
 *
 * Rule: a value the backend GENERATES (hash-derived demo/world data) must not
 * render as an unqualified headline KPI. The marker condition reads the
 * PROVENANCE ATTRIBUTE ON THE RESPONSE DTO that owns the metric — never a
 * hardcoded list of metric field names in screen code (which would silently go
 * wrong the moment a metric becomes real).
 *
 * The kit does not yet carry the provenance field (verified absent from
 * api-contracts + api-mocks @ 93196e63); Voyage ships it "alongside the
 * existing source tag" and names it in the carrying changelog. Until then
 * `provenanceOf` returns null for every DTO and no badge renders — exactly the
 * mock's "measured" state. The reader is alias-tolerant across the plausible
 * attribute spellings of that ONE field so it lights up on landing; tighten to
 * the single named attribute when Voyage's changelog names it.
 */
import type { ReactNode } from "react";
import { T } from "@v0y4ge/jrni-ui";
import { t as i18nT } from "./i18n";

const GENERATED_VALUES = new Set(["generated", "synthetic", "demo", "simulated"]);

/** Read the provenance attribute off a response DTO (or a nested stats object).
 *  Returns "generated" when the DTO declares its values synthesized, "measured"
 *  when it declares them real, null when the DTO carries no provenance yet. */
export function provenanceOf(dto: unknown): "generated" | "measured" | null {
  if (!dto || typeof dto !== "object") return null;
  const d = dto as Record<string, unknown>;
  const raw = d.provenance ?? d.__provenance ?? d.dataProvenance ?? d.data_provenance;
  if (typeof raw !== "string") return null;
  return GENERATED_VALUES.has(raw.toLowerCase()) ? "generated" : "measured";
}

/** Compact "Demo data" marker. Renders ONLY when the owning DTO declares its
 *  metrics generated; measured or provenance-less DTOs render nothing. Place it
 *  beside the metric label/value: `<DemoDataBadge of={responseDto}/>`. */
export function DemoDataBadge({ of, style }: { of: unknown; style?: React.CSSProperties }): ReactNode {
  if (provenanceOf(of) !== "generated") return null;
  return (
    <span
      title={i18nT("lib.provenance.demoDataTooltip", "Generated demo value — not measured tenant data. The backend marks this metric's source as synthetic.")}
      style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "1px 6px", borderRadius: 999, background: T.orange2, border: `1px solid ${T.orange1}40`, color: T.orange1, fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", verticalAlign: "middle", whiteSpace: "nowrap", cursor: "help", ...style }}>
      <span style={{ width: 5, height: 5, borderRadius: 999, background: T.orange1 }} />
      {i18nT("lib.provenance.demoData", "Demo data")}
    </span>
  );
}
